/* =========================================================================
 * The rtc notification inbox — the app's general "bell icon" feed, carrying
 * KYC, deposit/withdrawal, security and admin-announcement items.
 *
 * Two conventions from this gateway that differ from the rest of the app:
 *
 * - **int64 fields render as JSON strings.** `total`, `totalPages`,
 *   `unreadCount` and `markedCount` arrive as `"137"`, not `137`. Use the
 *   `…Value` helpers below.
 * - **Zero and false fields are omitted entirely** (proto3 JSON), so every
 *   field needs a default: an empty inbox returns `{"data":{}}`, not a payload
 *   full of zeros.
 * ========================================================================= */

export interface NotificationItem {
  notificationId: string;
  /** Stable machine key (`kyc_finalize`, `admin_manual`, …). Branch on this, never on the title. */
  type?: string | null;
  /** Already-rendered plain text — never render as HTML. */
  title: string;
  body: string;
  /** Extra data; today the only key ever set is `action`, on admin announcements. */
  payload?: Record<string, string>;
  /** `null`/absent is the unread flag. */
  readAt?: string | null;
  createdAt: string;
}

/** `readAt != null` is the read flag — there is no mutable `isRead` field. */
export const isNotificationRead = (notification: NotificationItem): boolean =>
  !!notification.readAt;

/**
 * A relative in-app path such as `/wallet/orders/123`. The server rejects
 * absolute URLs, anything containing `:` and anything with whitespace at write
 * time, but route it through the app's own router rather than `window.open`.
 */
export const notificationAction = (
  notification: NotificationItem
): string | undefined => notification.payload?.action;

/** `GET rtc/inbox/notifications?page_size=&page=` — 1-based pages, no cursor. */
export interface GetNotificationsRequest {
  page: number;
  pageSize?: number;
}

export interface NotificationsPage {
  notifications?: NotificationItem[] | null;
  /** int32 — a real JSON number, unlike `total`. */
  page?: number;
  pageSize?: number;
  /** int64 rendered as a string; the caller's whole inbox, identical on every page. */
  total?: string;
  /** int64 rendered as a string; `ceil(total / pageSize)`, and "0" for an empty inbox. */
  totalPages?: string;
  /** Derived server-side, so a client never recomputes page boundaries. */
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export interface GetNotificationsResponse {
  data?: NotificationsPage;
}

/** `GET rtc/inbox/unread-count` */
export interface GetUnreadCountResponse {
  data?: {
    unreadCount?: string;
  };
}

/** `POST rtc/inbox/read-all` */
export interface ReadAllNotificationsResponse {
  data?: {
    /** How many rows flipped from unread to read; `"0"` is success, not an error. */
    markedCount?: string;
  };
}

/** The int64-as-string fields all go through here. */
export const numericValue = (value?: string | number | null): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = parseInt(value || '0', 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/* =========================================================================
 * The single event the rtc inbox socket sends today.
 *
 * The socket payload is hand-marshalled on the server and is deliberately
 * **snake_case**, unlike the camelCase REST responses — and `unread_count` is a
 * real JSON number here, where REST sends the same value as a string. Do not
 * reuse the REST models.
 *
 * `body` and `payload` are intentionally absent: the event carries just enough
 * to update a badge and show a toast. Fetch over REST to render the full row.
 * ========================================================================= */

export const INBOX_EVENT_NOTIFICATION_CREATED = 'notification.created';

export interface InboxSocketEvent {
  /** Always `notification.created` today. Branch on this so new kinds stay additive. */
  event: string;
  notification_id: string;
  type?: string | null;
  title: string;
  created_at: string;
  /** Server-computed and authoritative at that instant. */
  unread_count: number;
}
